import { palette } from '../../styles/tokens';
import { Card } from '../common/Card';
import type { Business } from '../../types';

interface Props {
  business: Business;
  teamTotal: number;
}

const TIER_INFO: Record<string, { name: string; price: string; limit: number | null; bg: string; fg: string }> = {
  free:     { name: 'Free',     price: '$0/mo',      limit: 5,    bg: palette.g100,      fg: palette.g600 },
  trial:    { name: 'Trial',    price: '$0/mo',      limit: null, bg: palette.amberWash,  fg: palette.amber },
  pro:      { name: 'Pro',      price: '$39.99/mo',  limit: 50,   bg: palette.accentWash,  fg: palette.accentDeep },
  business: { name: 'Business', price: '$99.99/mo',  limit: null, bg: palette.greenWash,  fg: palette.green },
};

const ROW: React.CSSProperties = { display: 'flex', alignItems: 'center', padding: '10px 0', borderBottom: `1px solid ${palette.g100}` };
const LABEL: React.CSSProperties = { fontSize: 13, color: palette.g500, width: 160, flexShrink: 0 };
const VALUE: React.CSSProperties = { fontSize: 13.5, fontWeight: 500, color: palette.g800, flex: 1 };

export function BillingSection({ business, teamTotal }: Props) {
  const tier = TIER_INFO[business.tier] ?? { name: business.tier, price: '—', limit: null, bg: palette.g100, fg: palette.g600 };

  // Trial calculations
  const isTrial = business.tier === 'trial' && business.trial_ends_at;
  let trialDays = 0;
  let trialColor: string = palette.green;
  if (isTrial && business.trial_ends_at) {
    trialDays = Math.ceil((new Date(business.trial_ends_at).getTime() - Date.now()) / 86400000);
    if (trialDays < 3) trialColor = palette.red;
    else if (trialDays < 7) trialColor = palette.amber;
  }

  const teamPct = tier.limit ? Math.min(100, Math.round(teamTotal / tier.limit * 100)) : 0;

  const handleManage = () => {
    if (business.stripe_customer_id) {
      window.open('https://billing.stripe.com/p/login/test', '_blank');
    }
  };

  return (
    <Card title="Billing & Subscription">
      {/* Plan badge */}
      <div style={ROW}>
        <div style={LABEL}>Current plan</div>
        <div style={{ ...VALUE, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ padding: '3px 12px', borderRadius: 4, fontSize: 12, fontWeight: 600, background: tier.bg, color: tier.fg }}>{tier.name}</span>
          <span style={{ fontSize: 13, color: palette.g500 }}>{tier.price}</span>
        </div>
      </div>

      {/* Trial countdown */}
      {isTrial && (
        <div style={ROW}>
          <div style={LABEL}>Trial ends</div>
          <div style={{ ...VALUE, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13.5, fontWeight: 600, color: trialColor }}>
              {trialDays > 0 ? `${trialDays} day${trialDays !== 1 ? 's' : ''} remaining` : 'Expired'}
            </span>
            <span style={{ fontSize: 12, color: palette.g400 }}>
              ({new Date(business.trial_ends_at!).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })})
            </span>
          </div>
        </div>
      )}

      {/* Team count with bar */}
      <div style={ROW}>
        <div style={LABEL}>Team size</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13.5, fontWeight: 500, color: palette.g800, marginBottom: 4 }}>
            {teamTotal}{tier.limit ? ` / ${tier.limit}` : ''} team members
            {!tier.limit && <span style={{ fontSize: 11, color: palette.g400, marginLeft: 4 }}>(unlimited)</span>}
          </div>
          {tier.limit && (
            <div style={{ height: 4, borderRadius: 2, background: palette.g200, width: 180 }}>
              <div style={{ height: 4, borderRadius: 2, background: teamPct > 90 ? palette.red : teamPct > 70 ? palette.amber : palette.accent, width: `${teamPct}%`, transition: 'width 0.3s' }} />
            </div>
          )}
        </div>
      </div>

      {/* Payroll addon */}
      <div style={ROW}>
        <div style={LABEL}>Payroll add-on</div>
        <div style={{ ...VALUE, color: business.payroll_addon ? palette.green : palette.g400 }}>
          {business.payroll_addon ? (
            <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: palette.greenWash, color: palette.green }}>Active</span>
          ) : 'Not subscribed'}
        </div>
      </div>

      {/* Stripe customer ID */}
      {business.stripe_customer_id && (
        <div style={{ ...ROW, borderBottom: 'none' }}>
          <div style={LABEL}>Stripe customer</div>
          <div style={{ ...VALUE, fontSize: 12, color: palette.g400, fontFamily: 'monospace' }}>{business.stripe_customer_id}</div>
        </div>
      )}

      {/* Manage button */}
      <div style={{ marginTop: 16 }}>
        <button onClick={handleManage} style={{
          padding: '8px 20px', borderRadius: 6, border: `1px solid ${palette.g200}`,
          background: palette.white, fontSize: 13, fontWeight: 500, color: palette.accent,
          cursor: business.stripe_customer_id ? 'pointer' : 'default',
          fontFamily: 'inherit', transition: 'all 0.12s',
          opacity: business.stripe_customer_id ? 1 : 0.5,
        }}
          onMouseEnter={e => { if (business.stripe_customer_id) e.currentTarget.style.borderColor = palette.accent; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = palette.g200; }}
        >
          Manage Subscription &rarr;
        </button>
        {!business.stripe_customer_id && (
          <span style={{ fontSize: 11, color: palette.g400, marginLeft: 8 }}>Set up billing to manage your subscription</span>
        )}
      </div>
    </Card>
  );
}
